import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import { logger } from '../../../utils/debug/logger.js';

/**
 * Manages the display and clustering of markers on a Google Map
 */
class MarkerDisplayManager {
    constructor() {
        // Map of layers containing markers (key: layerName, value: Map of markers)
        this.layers = new Map();
        // Google Maps instance (set by initializeCluster) — used by isInitialized().
        this.map = null;
        // Cluster instance for grouping markers
        this.cluster = null;
        // Set of currently active markers
        this.activeMarkers = new Set();
        // Cache for marker configurations
        this.markerConfigs = new Map();
        // Active spiderfy state ({ moved, lines, listeners }) or null.
        this._spider = null;
        // Optional per-layer override for cluster clicks: (cluster, map) => void.
        // When set, it fully replaces the default fitBounds/spiderfy behavior.
        this.clusterClickHandler = null;
    }

    /**
     * Check if the manager is properly initialized
     */
    isInitialized() {
        return this.map !== null && this.cluster !== null;
    }

    /** Force the clusterer to redraw (replaces faking a 'zoom_changed' event). */
    refreshCluster() {
        if (this.cluster && typeof this.cluster.render === 'function') this.cluster.render();
    }

    /**
     * Diagonal span of `bounds` in CSS pixels at a given zoom level, using the map
     * projection (zoom-independent world coords scaled by 2^zoom). Returns null if
     * the projection is not ready.
     */
    _spanInPixels(map, bounds, zoom) {
        const proj = map.getProjection && map.getProjection();
        if (!proj) return null;
        const p1 = proj.fromLatLngToPoint(bounds.getNorthEast());
        const p2 = proj.fromLatLngToPoint(bounds.getSouthWest());
        if (!p1 || !p2) return null;
        const scale = Math.pow(2, zoom);
        return Math.hypot((p1.x - p2.x) * scale, (p1.y - p2.y) * scale);
    }

    /**
     * Fan co-located markers out in a circle (spiral beyond ~10) around `center`,
     * with leader lines, so each becomes individually clickable. Collapses on the
     * next zoom change or map click. Used when no zoom level can separate markers
     * sharing (nearly) the same coordinates.
     */
    _spiderfy(map, markers, center) {
        this._unspiderfy();
        const proj = map.getProjection && map.getProjection();
        if (!proj) { map.setZoom((map.getZoom() || 0) + 2); return; }

        const scale = Math.pow(2, map.getZoom());
        const centerWorld = proj.fromLatLngToPoint(center);
        const n = markers.length;
        const moved = [];
        const lines = [];

        markers.forEach((m, i) => {
            const angle = (2 * Math.PI * i) / n;
            const radiusPx = n <= 10 ? 16 + n * 6 : 24 + i * 5; // circle, then spiral
            const wx = centerWorld.x + (radiusPx * Math.cos(angle)) / scale;
            const wy = centerWorld.y + (radiusPx * Math.sin(angle)) / scale;
            const latLng = proj.fromPointToLatLng(new google.maps.Point(wx, wy));

            moved.push({ marker: m, original: m.position });
            m.position = latLng;
            m.map = map; // reveal (clusterer had hidden it)

            lines.push(new google.maps.Polyline({
                map,
                path: [center, latLng],
                strokeColor: '#ec4899',
                strokeOpacity: 0.7,
                strokeWeight: 1.5,
                zIndex: 800,
            }));
        });

        const listeners = [
            google.maps.event.addListenerOnce(map, 'zoom_changed', () => this._unspiderfy()),
            google.maps.event.addListenerOnce(map, 'click', () => this._unspiderfy()),
        ];
        this._spider = { moved, lines, listeners };
    }

    /** Collapse an active spiderfy: remove lines, restore positions, re-cluster. */
    _unspiderfy() {
        if (!this._spider) return;
        const { moved, lines, listeners } = this._spider;
        listeners.forEach(l => { try { google.maps.event.removeListener(l); } catch { /* */ } });
        lines.forEach(l => l.setMap(null));
        moved.forEach(({ marker, original }) => { marker.position = original; });
        this._spider = null;
        this.refreshCluster();
    }

    /**
     * Initialize the marker clusterer with specific configuration
     * @param {google.maps.Map} map - Google Maps instance
     * @param {Function} renderFn - Function to render cluster markers
     */
    initializeCluster(map, renderFn) {
        this.map = map;
        if (this.cluster) {
            logger.map.warn("⚠️ Cluster already exists, clearing markers...");
            this.cluster.clearMarkers();
        } else {
            logger.map.info("🚀 Creating new Google Maps cluster");
            this.cluster = new MarkerClusterer({
                map,
                markers: [],
                algorithm: new SuperClusterAlgorithm({
                    radius: 60,
                    maxZoom: 15,
                    minPoints: 2,
                    minZoom: 1,
                }),
                onClusterClick: (event, cluster, map) => {
                    // Per-layer override (e.g. the Photos layer shows a thumbnail strip).
                    if (typeof this.clusterClickHandler === 'function') {
                        this.clusterClickHandler(cluster, map);
                        return;
                    }
                    const markers = cluster.markers || [];
                    const bounds = new google.maps.LatLngBounds();
                    markers.forEach(m => { if (m.position) bounds.extend(m.position); });
                    if (bounds.isEmpty()) {
                        map.setZoom((map.getZoom() || 0) + 2);
                        map.setCenter(cluster.position);
                        return;
                    }

                    // Decide between zooming and spiderfying using the markers'
                    // geographic span measured in pixels AT a deep zoom (16): if even
                    // then they'd stay within a few pixels, no zoom can separate them
                    // → fan them out (spiderfy). Otherwise fitBounds reveals them.
                    const spanPx16 = this._spanInPixels(map, bounds, 16);
                    if (markers.length > 1 && (spanPx16 == null || spanPx16 < 35)) {
                        this._spiderfy(map, markers, cluster.position);
                    } else {
                        map.fitBounds(bounds, 80);
                        google.maps.event.addListenerOnce(map, 'idle', () => {
                            if (map.getZoom() > 17) map.setZoom(17);
                        });
                    }
                },
                renderer: {
                    // Pass the whole Cluster (count, position, markers) so renderers
                    // that need the underlying markers (e.g. the Photos hover strip)
                    // can reach them; town layers just read count/position.
                    render: (cluster) => renderFn(cluster)
                }
            });
        }

        logger.map.info("🚀 Google Maps cluster initialized");
    }

    /**
     * Add a new layer to manage markers
     * @param {string} layerName - Name of the layer
     */
    addLayer(layerName) {
        if (!this.layers.has(layerName)) {
            this.layers.set(layerName, new Map());
            logger.map.debug(`Created new layer: ${layerName}`);
        }
    }

    /**
 * Add a marker to a specific layer with improved validation
 * @param {string} layerName - Target layer
 * @param {string} key - Unique identifier for the marker
 * @param {google.maps.LatLng} position - Marker position
 * @param {Object} options - Marker options
 * @param {Function} onClickCallback - Click event handler
 * @returns {google.maps.marker.AdvancedMarkerElement|null} - Created marker or null if invalid
 */
    addMarker(layerName, key, position, options, onClickCallback = null) {
        // Validation des paramètres essentiels
        if (!layerName) {
            logger.map.warn('⚠️ Nom de calque manquant pour addMarker');
            return null;
        }

        if (!key) {
            logger.map.warn('⚠️ Clé d\'identifiant manquante pour addMarker');
            return null;
        }

        // Vérifier que la position est une instance valide de LatLng
        if (!position || !(position instanceof google.maps.LatLng)) {
            logger.map.warn(`⚠️ Position invalide pour le marqueur ${key}`, position);
            return null;
        }

        // Vérifier que les options contiennent au moins le contenu ou le titre
        if (!options || (!options.content && !options.title)) {
            logger.map.warn(`⚠️ Options invalides pour le marqueur ${key}`);
            return null;
        }

        // Créer la couche si elle n'existe pas
        this.addLayer(layerName);
        const layerMarkers = this.layers.get(layerName);

        // Vérifier si le marqueur existe déjà
        if (layerMarkers.has(key)) {
            return layerMarkers.get(key);
        }

        try {
            // Créer le marqueur avec gestion des erreurs
            const marker = new google.maps.marker.AdvancedMarkerElement({
                position,
                map: null, // Initialement invisible
                ...options
            });

            // Ajouter l'écouteur de clic si fourni
            if (onClickCallback && typeof onClickCallback === 'function') {
                marker.addListener('click', () => onClickCallback(marker));
            }

            layerMarkers.set(key, marker);
            return marker;
        } catch (error) {
            logger.map.error(`❌ Erreur lors de la création du marqueur ${key}:`, error);
            return null;
        }
    }

    /**
 * Add markers to the cluster with incremental approach
 * @param {google.maps.Map} map - Google Maps instance
 * @returns {boolean} Success status
 */
    addMarkersToCluster(map) {
        if (!this.isInitialized()) {
            logger.map.warn('⚠️ Cluster or map not initialized');
            return false;
        }

        // Collect currently visible markers. A marker the clusterer has folded
        // into a cluster has map=null but is still "shown" (as part of the cluster
        // badge) — count it as visible, otherwise the incremental diff below
        // removes clustered markers (e.g. two co-located photos) and the cluster
        // badge vanishes on a second addMarkersToCluster pass.
        const inCluster = new Set(this.cluster.markers || []);
        const currentlyVisibleMarkers = new Set();
        this.layers.forEach((layerMarkers) => {
            layerMarkers.forEach(marker => {
                if (marker.map !== null || inCluster.has(marker)) {
                    currentlyVisibleMarkers.add(marker);
                }
            });
        });

        logger.map.debug(`📊 Found ${currentlyVisibleMarkers.size} visible markers`);

        // If no visible markers, we can skip the rest
        if (currentlyVisibleMarkers.size === 0) {
            logger.map.warn('⚠️ No markers to display in cluster');
            return false;
        }

        // If the cluster has no markers yet, simply add all visible ones
        if (!this.cluster.markers || this.cluster.markers.length === 0) {
            logger.map.debug(`📊 Adding ${currentlyVisibleMarkers.size} markers to empty cluster`);
            this.cluster.addMarkers([...currentlyVisibleMarkers]);
            return true;
        }

        // Calculate differences with current cluster state
        const currentClusterMarkers = new Set(this.cluster.markers);

        // Markers to add: visible but not in cluster
        const markersToAdd = [...currentlyVisibleMarkers].filter(
            marker => !currentClusterMarkers.has(marker)
        );

        // Markers to remove: in cluster but not visible anymore
        const markersToRemove = [...currentClusterMarkers].filter(
            marker => !currentlyVisibleMarkers.has(marker)
        );

        logger.map.debug(`🔄 Updating cluster: +${markersToAdd.length} -${markersToRemove.length}`);

        try {
            // Apply changes incrementally
            if (markersToRemove.length > 0) {
                this.cluster.removeMarkers(markersToRemove);
            }

            if (markersToAdd.length > 0) {
                this.cluster.addMarkers(markersToAdd);
            }

            // Force a clustering refresh if needed
            if (markersToAdd.length > 0 || markersToRemove.length > 0) {
                this.refreshCluster();
            }

            logger.map.info('✅ Cluster updated successfully');
            return true;
        } catch (error) {
            logger.map.error('❌ Error updating cluster:', error);

            // Fallback to full reset in case of error
            try {
                logger.map.warn('⚠️ Attempting fallback to full cluster reset');
                this.cluster.clearMarkers();
                this.cluster.addMarkers([...currentlyVisibleMarkers]);
                return true;
            } catch (fallbackError) {
                logger.map.error('❌ Fallback failed:', fallbackError);
                return false;
            }
        }
    }

    /**
     * Toggle visibility of a layer
     * @param {string} layerName - Target layer
     * @param {boolean} visible - Visibility state
     * @param {google.maps.Map} map - Google Maps instance
     */
    toggleLayerVisibility(layerName, visible, map) {
        logger.map.info(`Toggling visibility for layer ${layerName} to ${visible}`);
        const layerMarkers = this.layers.get(layerName);
        if (layerMarkers) {
            if (this.cluster && !visible) {
                this.cluster.clearMarkers();
            }

            layerMarkers.forEach(marker => {
                marker.map = visible ? map : null;
            });

            if (visible && map) {
                logger.map.debug(`🔄 Mise à jour du clustering pour ${layerName}`);
                this.addMarkersToCluster(map);
                // Forcer un rafraîchissement du clustering
                this.refreshCluster();
            }
        }
    }

    /**
 * Clear markers from specific layer or all layers with proper event cleanup
 * @param {string} layerName - Target layer (optional)
 */
    clearMarkers(layerName = null) {
        logger.map.debug(`🧹 Nettoyage des marqueurs${layerName ? ` pour la couche ${layerName}` : ' pour toutes les couches'}`);

        // Collapse any active spiderfy first (removes leader lines, restores positions).
        this._unspiderfy();

        if (this.cluster) {
            // Clear the cluster's markers but keep the clusterer ATTACHED to the
            // map — detaching it (setMap(null)) without re-attaching left clustering
            // running on borrowed state after every layer rebuild.
            this.cluster.clearMarkers();
        }

        if (layerName) {
            const layerMarkers = this.layers.get(layerName);
            if (layerMarkers) {
                layerMarkers.forEach(marker => {
                    // Supprimer tous les écouteurs avant de retirer de la carte
                    google.maps.event.clearInstanceListeners(marker);
                    marker.map = null;
                });
                this.layers.delete(layerName);
            }
        } else {
            this.layers.forEach(layerMarkers => {
                layerMarkers.forEach(marker => {
                    // Supprimer tous les écouteurs avant de retirer de la carte
                    google.maps.event.clearInstanceListeners(marker);
                    marker.map = null;
                });
            });
            this.layers.clear();
        }

        this.activeMarkers.clear();
        this.markerConfigs.clear();
    }

    /**
 * Create marker configuration with improved validation
 * @param {string} townName - Name of the town
 * @param {Object} townData - Town data
 * @param {Function} createMarkerElementFn - Function to create marker element
 * @returns {Object|null} Marker configuration or null if invalid data
 */
    createMarkerConfig(townName, townData, createMarkerElementFn) {
        // Validation complète des données d'entrée
        if (!townData) {
            logger.map.warn(`⚠️ Données manquantes pour la ville ${townName}`);
            return null;
        }

        // Vérification que les coordonnées existent et sont numériques
        if (!townData.latitude || !townData.longitude ||
            isNaN(Number(townData.latitude)) || isNaN(Number(townData.longitude))) {
            logger.map.warn(`⚠️ Coordonnées invalides pour ${townName}: (${townData.latitude}, ${townData.longitude})`);
            return null;
        }

        // Vérification des limites des coordonnées (valeurs plausibles)
        const lat = Number(townData.latitude);
        const lng = Number(townData.longitude);

        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            logger.map.warn(`⚠️ Coordonnées hors limites pour ${townName}: (${lat}, ${lng})`);
            return null;
        }

        // Création de la configuration avec les coordonnées validées
        const config = {
            position: new google.maps.LatLng(lat, lng),
            options: {
                content: createMarkerElementFn(townData),
                title: townData.townDisplay || townData.town || townName
            }
        };

        this.markerConfigs.set(townName, config);
        return config;
    }

    /**
 * Get existing marker or create new one with improved validation
 * @param {string} layerName - Target layer
 * @param {string} townName - Name of the town
 * @param {Object} townData - Town data
 * @param {Function} createMarkerElementFn - Function to create marker element
 * @param {Function} onClickCallback - Click event handler
 * @returns {google.maps.marker.AdvancedMarkerElement|null} - The marker or null if invalid
 */
    getOrCreateMarker(layerName, townName, townData, createMarkerElementFn, onClickCallback) {
        // Validation des paramètres essentiels
        if (!layerName) {
            logger.map.warn('⚠️ Nom de calque manquant pour getOrCreateMarker');
            return null;
        }

        if (!townName) {
            logger.map.warn('⚠️ Identifiant de ville manquant pour getOrCreateMarker');
            return null;
        }

        if (!createMarkerElementFn || typeof createMarkerElementFn !== 'function') {
            logger.map.warn(`⚠️ Fonction de création d'élément invalide pour ${townName}`);
            return null;
        }

        // Vérifier que les données de ville existent
        if (!townData) {
            logger.map.warn(`⚠️ Données manquantes pour la ville ${townName}`);
            return null;
        }

        // Utiliser la configuration existante ou en créer une nouvelle
        let config = this.markerConfigs.get(townName);

        if (!config) {
            config = this.createMarkerConfig(townName, townData, createMarkerElementFn);
            if (!config) {
                logger.map.warn(`⚠️ Impossible de créer la configuration pour ${townName}`);
                return null;
            }
        }

        // Ajouter le marqueur avec la configuration validée
        return this.addMarker(
            layerName,
            townName,
            config.position,
            config.options,
            onClickCallback
        );
    }

    /**
 * Cleanup resources with proper event handling
 */
    cleanup() {
        this._unspiderfy();
        this.layers.forEach((layerMarkers, layerName) => {
            layerMarkers.forEach(marker => {
                // Supprimer les écouteurs d'événements
                google.maps.event.clearInstanceListeners(marker);
                marker.map = null;
            });
        });

        this.clearMarkers();

        if (this.cluster) {
            // Supprimer les écouteurs du cluster également
            if (this.cluster.onClusterClick) {
                google.maps.event.clearListeners(this.cluster, 'click');
            }
            this.cluster.setMap(null);
        }

        this.cluster = null;
        this.activeMarkers.clear();
        this.markerConfigs.clear();
    }
}

export default MarkerDisplayManager;
